import { useEffect, useRef, useState } from 'react';
import { SetTimeoutResult } from 'src/utils/UtilityTypes';

interface Params {
	numBanners: number;
	rotationIntervalMs: number;
}

/**
 * Sets up the following carousel functionality for banners:
 * - Automatically rotates through banners after a delay
 * - Pauses rotation timing when banner hovered
 * - Resumes rotation timing when banner not hovered
 * - Resets rotation timing when step indicator is clicked
 */
export default function useBannerCarousel({
	numBanners,
	rotationIntervalMs,
}: Params) {
	const [activeIdx, setActiveIdx] = useState(0);
	const timeoutIdRef = useRef<SetTimeoutResult>(null);
	const lastStartTimeRef = useRef<number | null>(null);
	const remainingTimeRef = useRef<number>(rotationIntervalMs);
	const isHoveredRef = useRef(false);

	function clearRotationTimeout() {
		if (timeoutIdRef.current !== null) {
			clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
	}

	function scheduleRotationTimeout(delay: number) {
		clearRotationTimeout();

		if (numBanners <= 1) {
			return;
		}

		if (isHoveredRef.current) {
			remainingTimeRef.current = delay;
			return;
		}

		remainingTimeRef.current = delay;
		lastStartTimeRef.current = performance.now();

		timeoutIdRef.current = setTimeout(() => {
			setActiveIdx((prev) => (prev + 1) % numBanners);
			scheduleRotationTimeout(rotationIntervalMs);
		}, delay);
	}

	useEffect(() => {
		if (numBanners <= 1) {
			setActiveIdx(0);
			clearRotationTimeout();
			return;
		}

		scheduleRotationTimeout(rotationIntervalMs);

		return () => {
			clearRotationTimeout();
		};
	}, [numBanners]);

	function handleMouseEnter() {
		isHoveredRef.current = true;

		if (timeoutIdRef.current !== null && lastStartTimeRef.current !== null) {
			const elapsed = performance.now() - lastStartTimeRef.current;
			const remaining = Math.max(0, rotationIntervalMs - elapsed);
			remainingTimeRef.current = remaining;
			clearRotationTimeout();
		}
	}

	function handleMouseLeave() {
		isHoveredRef.current = false;

		if (numBanners <= 1) {
			return;
		}

		if (remainingTimeRef.current <= 0) {
			setActiveIdx((prev) => (prev + 1) % numBanners);
			scheduleRotationTimeout(rotationIntervalMs);
			return;
		}

		scheduleRotationTimeout(remainingTimeRef.current);
	}

	function handleStepClick(step: number) {
		setActiveIdx(step);
		remainingTimeRef.current = rotationIntervalMs;

		if (!isHoveredRef.current) {
			scheduleRotationTimeout(rotationIntervalMs);
		}
	}

	return {
		activeIdx,
		handleMouseEnter,
		handleMouseLeave,
		handleStepClick,
	};
}
